import React from "react";
import Link from "next/link";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { SectionHeader } from "../components/section-header";
import { SectionLoader } from "../components/section-loader";
import { EmptyState } from "@/components/shared/empty-state";
import { FileText, Download, ArrowRight } from "lucide-react";
import type { HomepageReport } from "@/components/homepage/types";

interface ResearchSpotlightSectionProps {
  items: HomepageReport[];
  isLoading: boolean;
}

export function ResearchSpotlightSection({ items, isLoading }: ResearchSpotlightSectionProps) {
  const displayItems = items.length > 0 ? items.slice(0, 4) : [];

  if (!displayItems.length && !isLoading) return null;

  return (
    <section className="border-b border-border" aria-labelledby="research-heading">
      <div className="mx-auto max-w-7xl px-5 py-16 sm:py-20">
        <SectionHeader
          eyebrow="Research"
          icon={FileText}
          title="Research Spotlight"
          href="/research"
        />
        {isLoading ? (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="space-y-3">
                <SectionLoader variant="card" />
              </div>
            ))}
          </div>
        ) : displayItems.length > 0 ? (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {displayItems.map((item) => (
              <Card key={item.id} className="group hover:border-neutral-300 transition-all duration-150">
                <CardContent className="p-5">
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary mb-4">
                    <FileText className="h-6 w-6" />
                  </div>
                  <h3 className="text-sm font-heading font-semibold text-foreground group-hover:text-primary transition-colors line-clamp-2">
                    {item.title}
                  </h3>
                  <p className="mt-2 text-xs text-muted-foreground leading-relaxed line-clamp-2">
                    {typeof item.description === "string" ? item.description.slice(0, 100) : ""}
                  </p>
                  <div className="mt-4 flex items-center gap-2 text-xs text-muted-foreground">
                    <Download className="h-3.5 w-3.5" />
                    <span>{item.downloadCount ?? 0} downloads</span>
                  </div>
                  <Button variant="ghost" size="sm" asChild className="mt-4 p-0 h-auto text-xs gap-1">
                    <Link href={`/research`}>
                      <Download className="h-3 w-3" /> Download PDF
                    </Link>
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <EmptyState icon={FileText} description="No research available yet." />
        )}
      </div>
    </section>
  );
}